import type { Unsubscribe } from 'firebase/firestore';

interface Subscription<T> {
  unsubscribe: Unsubscribe;
  callbacks: Set<(data: T) => void>;
  errorCallbacks: Set<(error: Error) => void>;
}

export function createSubscription<K extends string, T>(
  subscribe: (key: K, onData: (data: T) => void, onError: (error: Error) => void) => Unsubscribe,
): (key: K, onUpdate: (data: T) => void, onError?: (error: Error) => void) => Unsubscribe {
  const state: Partial<Record<K, Subscription<T>>> = {};

  return (key, onUpdate, onError) => {
    if (!state[key]) {
      const callbacks = new Set<(data: T) => void>();
      const errorCallbacks = new Set<(error: Error) => void>();

      state[key] = {
        unsubscribe: subscribe(
          key,
          (data) => callbacks.forEach((cb) => void cb(data)),
          (error) => {
            // An errored snapshot listener is permanently dead — evict it from the cache so
            // the next subscriber re-establishes a fresh listener instead of joining a corpse.
            const entry = state[key];
            delete state[key];
            entry?.errorCallbacks.forEach((cb) => void cb(error));
          },
        ),
        callbacks,
        errorCallbacks,
      };
    }

    state[key].callbacks.add(onUpdate);
    if (onError) state[key].errorCallbacks.add(onError);

    return () => {
      const entry = state[key];
      if (!entry) return;

      entry.callbacks.delete(onUpdate);
      if (onError) entry.errorCallbacks.delete(onError);

      if (entry.callbacks.size === 0) {
        entry.unsubscribe();

        delete state[key];
      }
    };
  };
}
